import React from 'react';
import { Form, Input, InputNumber, Card, Spin, Upload, Image, Space, Alert, Row, Col } from 'antd';
import { UploadOutlined, SaveOutlined } from '@ant-design/icons';

import PageLayout from '@components/layout/PageLayout';

import styles from './NspCoreInfoPage.module.css';
import useNspCoreInfoPage from './useNspCoreInfoPage';
import { NSPButton } from '@components/ui/NSPButton';
import useSession from '@hooks/useSession';


const NspCoreInfoPage = () => {
  const { isAdmin, isSuperAdmin } = useSession();
  const isReadOnly = isAdmin && !isSuperAdmin;

  const {
    form,
    selectedEntry,
    loading,
    updating,
    handleSave,
    handleLogoUpload,
    fileList,
    uploadingLogo,
    setFileList,
    saveFeedback,
  } = useNspCoreInfoPage();

  if (loading) {
    return (
      <PageLayout title="NSP Core Information">
        <div className={styles.loadingContainer}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  if (!selectedEntry) {
    return (
      <PageLayout title="NSP Core Information">
        <Card>
          <p>No core information entries found. Please create an entry in the database first.</p>
        </Card>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="NSP Core Information">
      <Card className={styles.card}>
        {saveFeedback && (
          <Alert
            message={
              saveFeedback.status === 'success' ? 'Save Successful!' : 'Save Failed!'
            }
            description={saveFeedback.message}
            type={saveFeedback.status}
            showIcon
            closable
            style={{ marginBottom: 16 }}
          />
        )}
        <Form form={form} layout="vertical" onFinish={handleSave} className={styles.form} disabled={isReadOnly}>
          {/* Company Logo Section */}
          <div className={styles.logoSection}>
            <h3>Company Logo</h3>
            <Space direction="vertical" align="center">
              {selectedEntry.logoUrl && (
                <Image
                  src={selectedEntry.logoUrl}
                  alt="Company Logo"
                  width={200}
                  preview
                  className={styles.logoImage}
                />
              )}
              {!isReadOnly && (
                <Upload
                  beforeUpload={handleLogoUpload}
                  fileList={fileList}
                  onChange={({ fileList }) => setFileList(fileList)}
                  accept="image/*"
                  maxCount={1}
                  showUploadList={false}
                >
                  <NSPButton icon={<UploadOutlined />} loading={uploadingLogo}>
                    {selectedEntry.logoUrl ? 'Replace Logo' : 'Upload Logo'}
                  </NSPButton>
                </Upload>
              )}
            </Space>
          </div>

          {/* Company Information */}
          <h3>Company Information</h3>
          <Form.Item
            label="Company Name"
            name="companyName"
            rules={[{ required: true, message: 'Please enter company name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Address"
            name="address"
            rules={[{ required: true, message: 'Please enter address' }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="City"
                name="city"
                rules={[{ required: true, message: 'Please enter city' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Province"
                name="province"
                rules={[{ required: true, message: 'Please enter province' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Postal Code"
                name="postalCode"
                rules={[
                  { required: true, message: 'Please enter postal code' },
                  {
                    pattern: /^[A-Z]\d[A-Z] \d[A-Z]\d$/,
                    message: 'Please enter a valid Canadian postal code (e.g., A1A 1A1)',
                  },
                ]}
              >
                <Input placeholder="A1A 1A1" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Fax Number"
                name="faxNumber"
                rules={[
                  { required: true, message: 'Please enter fax number' },
                  {
                    pattern: /^(\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/,
                    message: 'Please enter a valid phone number (e.g., (123) 456-7890 or 123-456-7890)',
                  },
                ]}
              >
                <Input placeholder="(###) ###-####" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="HST Rate (%)"
                name="hstRate"
                rules={[
                  { required: true, message: 'Please enter HST rate' },
                  {
                    type: 'number',
                    min: 0,
                    max: 100,
                    message: 'HST rate must be between 0 and 100',
                  },
                ]}
              >
                <InputNumber style={{ width: '100%' }} precision={2} placeholder="15.00" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Email Address for Approver"
                name="approverEmail"
                rules={[
                  { required: true, message: 'Please enter approver email address' },
                  { type: 'email', message: 'Please enter a valid email address' },
                ]}
              >
                <Input placeholder="approver@example.com" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Email Address for Energy Risk Management Point of Contact"
                name="energyRiskManagementEmail"
                rules={[
                  { required: true, message: 'Please enter Energy Risk Management email address' },
                  { type: 'email', message: 'Please enter a valid email address' },
                ]}
              >
                <Input placeholder="energyrisk@example.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="NSP GL"
                name="nspGl"
                rules={[
                  { required: true, message: 'NSP GL is required' },
                  { pattern: /^[0-9.]+$/, message: 'Only numbers and periods are allowed' },
                ]}
              >
                <Input placeholder="e.g., 001.100100.010.1100.010.000.0000.000" />
              </Form.Item>
            </Col>
          </Row>

          {/* Contact Information */}
          <h3>Contact Information</h3>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Contact Name"
                name="contactName"
                rules={[{ required: true, message: 'Please enter contact name' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Contact Phone"
                name="contactPhone"
                rules={[
                  { required: true, message: 'Please enter contact phone' },
                  {
                    pattern: /^(\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/,
                    message: 'Please enter a valid phone number (e.g., (123) 456-7890 or 123-456-7890)',
                  },
                ]}
              >
                <Input placeholder="(###) ###-####" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Contact Fax"
                name="contactFax"
                rules={[
                  { required: true, message: 'Please enter contact fax' },
                  {
                    pattern: /^(\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/,
                    message: 'Please enter a valid phone number (e.g., (123) 456-7890 or 123-456-7890)',
                  },
                ]}
              >
                <Input placeholder="(###) ###-####" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Contact Email"
                name="contactEmail"
                rules={[
                  { required: true, message: 'Please enter contact email' },
                  { type: 'email', message: 'Please enter a valid email' },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {/* Save Button */}
          {!isReadOnly && (
            <Form.Item>
              <NSPButton
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={updating}
                size="large"
              >
                Save Changes
              </NSPButton>
            </Form.Item>
          )}
        </Form>
      </Card>
    </PageLayout>
  );
};

export default NspCoreInfoPage;
